import React, { useMemo, useRef, useState } from 'react'
import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { gql, useMutation, useQuery } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { exportSuperAdminCsv, superAdminDashboard, superAdminLeaderboard, superAdminOperations } from '../apollo'
import { C, FONT, cssVars, tabular } from '../components/RestaurantDashboard/tokens'
import { rangeLabel, timeIn, todayIn } from '../components/RestaurantDashboard/format'
import KpiCard from '../components/RestaurantDashboard/KpiCard'
import SaFilterBar from '../components/SuperAdminDashboard/SaFilterBar'
import { MoneyGrid, Waterfall } from '../components/SuperAdminDashboard/MoneyBreakdown'
import { OrderMix, RevenueOverTime } from '../components/SuperAdminDashboard/Trends'
import Leaderboard from '../components/SuperAdminDashboard/Leaderboard'
import { OperationsCards, RecentOrdersTable } from '../components/SuperAdminDashboard/Operations'
import { $, compareText } from '../components/SuperAdminDashboard/saUtil'

/**
 * Super admin dashboard — platform-wide money and operations.
 *
 * ONE filter drives every section. The page makes three independent queries
 * (summary, leaderboard, operations) so a slow one never blocks the others;
 * the server computes all three from one cached aggregate, which is why the
 * leaderboard's totals row equals the KPI cards to the cent. All money arrives
 * in integer cents and is only converted here, for display.
 */
const SUMMARY = gql`${superAdminDashboard}`
const LEADERBOARD = gql`${superAdminLeaderboard}`
const OPERATIONS = gql`${superAdminOperations}`
const EXPORT_CSV = gql`${exportSuperAdminCsv}`
const TZ = 'America/New_York'

const SuperAdminDashboard = () => {
  const today = todayIn(TZ)
  const [filter, setFilter] = useState({ preset: '30', from: null, to: null, compare: 'previous', restaurantIds: [], channel: 'ALL', fulfillment: 'ALL' })
  const variables = {
    filter: {
      preset: filter.preset,
      ...(filter.preset === 'custom' ? { from: filter.from, to: filter.to } : {}),
      compare: filter.preset === 'all' ? 'none' : filter.compare,
      restaurantIds: filter.restaurantIds,
      channel: filter.channel,
      fulfillment: filter.fulfillment
    }
  }
  const opts = { variables, fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true }
  const s = useQuery(SUMMARY, opts)
  const l = useQuery(LEADERBOARD, opts)
  const o = useQuery(OPERATIONS, opts)
  const [runExport, exportState] = useMutation(EXPORT_CSV)

  // Keep the last result on screen while the next one loads — no layout shift.
  const summary = (s.data || s.previousData)?.superAdminDashboard
  const board = (l.data || l.previousData)?.superAdminLeaderboard
  const ops = (o.data || o.previousData)?.superAdminOperations
  const loading = s.loading || l.loading || o.loading
  const error = s.error || l.error || o.error

  // Restaurant picker options: every restaurant, remembered from the first
  // unfiltered leaderboard (it lists restaurants with 0 orders too).
  const allRestaurants = useRef([])
  const restaurants = useMemo(() => {
    if (board && filter.restaurantIds.length === 0) {
      allRestaurants.current = board.rows.map(r => ({ id: r.restaurantId, name: r.name })).sort((a, b) => a.name.localeCompare(b.name))
    }
    return allRestaurants.current
  }, [board, filter.restaurantIds.length])

  const refresh = () => {
    const v = { ...variables, refresh: true }
    s.refetch(v); l.refetch(v); o.refetch(v)
  }
  const doExport = async () => {
    try {
      const res = await runExport({ variables })
      const csv = res?.data?.exportSuperAdminCsv
      if (csv) {
        // The server builds the CSV (same metric definitions as the page and
        // logs the export); write it out untouched.
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
        const a = document.createElement('a')
        a.href = url
        a.download = `rushmyfood-orders-${summary?.from || 'all'}-to-${summary?.to || today}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      }
    } catch (e) {
      window.alert(`Export failed: ${e.message}`)
    }
  }

  const c = summary?.current
  const p = summary?.previous
  const cmp = compareText(summary)
  const first = s.loading && !summary
  const spark = key => (summary?.series || []).map(pt => pt[key])
  const rec = summary?.reconciliation
  const noFinancials = summary && !summary.financialsTracked

  const recWarning = rec && !rec.ok ? (
    <Tooltip arrow placement="bottom-end" title={
      <Box sx={{ fontSize: 12 }}>
        <b>{rec.count} order{rec.count === 1 ? '' : 's'} don’t balance</b> (subtotal − discounts + fees + tax + tips ≠ amount charged):
        {rec.orders.slice(0, 12).map(u => <div key={u.id}>#{u.orderId}: parts {$(u.computed)} · charged {$(u.recorded)}</div>)}
        {rec.count > 12 ? <div>…and {rec.count - 12} more</div> : null}
      </Box>
    }>
      <WarningAmberRoundedIcon tabIndex={0} aria-label={`${rec.count} orders don't reconcile`} sx={{ fontSize: 20, color: '#B45309', cursor: 'help' }} />
    </Tooltip>
  ) : null

  return (
    <Box sx={{ ...cssVars, bgcolor: 'var(--page)', minHeight: '100vh', fontFamily: FONT, color: C.text, ...tabular }}>
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 4 }, pt: { xs: 2, md: 4 }, pb: 2, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography component="h1" sx={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2, color: C.text }}>Dashboard</Typography>
          <Typography sx={{ fontSize: 14, color: C.textMuted, mt: 0.5 }}>
            All restaurants · {summary ? rangeLabel(summary.from, summary.to) : ' '}{summary?.previous ? ` · ${cmp}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 13, color: C.textMuted }} aria-live="polite">
            {loading && summary ? 'Updating…' : summary ? `Last updated ${timeIn(summary.generatedAt, TZ)}` : ''}
          </Typography>
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={refresh} aria-label="Refresh" sx={{ border: `1px solid ${C.border}`, bgcolor: '#fff' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <SaFilterBar filter={filter} today={today} restaurants={restaurants} onChange={setFilter}
        onExport={doExport} exporting={exportState.loading} exportDisabled={!summary || noFinancials} />

      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 4 }, pb: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {error ? <Alert severity="error" sx={{ borderRadius: '12px' }}>Part of the dashboard could not load: {error.message}</Alert> : null}
        {noFinancials ? (
          <Alert severity="info" sx={{ borderRadius: '12px' }}>
            Money figures aren’t tracked for {filter.channel === 'LEGACY' ? 'legacy-app' : 'partner'} orders — they appear only as counts in Order mix.
          </Alert>
        ) : (
          <Typography sx={{ fontSize: 12.5, color: C.textMuted, mt: -1.5 }}>
            Money figures cover dashboard orders (online, embed and in-store). Legacy-app and partner orders appear as counts in Order mix.
          </Typography>
        )}

        {/* ROW 1 — headline KPIs */}
        <Box component="section" aria-label="Headline figures" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' } }}>
          <KpiCard label="Grand total (GMV)" hint="Everything customers paid: item subtotal − discounts + delivery fees + service fees + tax + tips."
            value={$(c?.grandTotal)} cur={c?.grandTotal} prev={p?.grandTotal} prevDisplay={$(p?.grandTotal)} fmtAbs={$}
            compareText={p ? cmp : ''} spark={spark('gmv')} loading={first} empty={!first && c && c.orders === 0} warning={recWarning} />
          <KpiCard label="Net platform revenue" hint="Commission + service fees + platform-kept delivery fees − platform-funded discounts − refunds the platform absorbed − processing fees."
            value={$(c?.netRevenue)} cur={c?.netRevenue} prev={p?.netRevenue} prevDisplay={$(p?.netRevenue)} fmtAbs={$}
            compareText={p ? cmp : ''} spark={spark('netRevenue')} loading={first} empty={!first && c && c.orders === 0} />
          <KpiCard label="Total orders" value={c ? c.orders.toLocaleString('en-US') : '—'} sub={c && c.orders ? `AOV ${$(c.aov)}` : null}
            cur={c?.orders} prev={p?.orders} prevDisplay={p ? p.orders.toLocaleString('en-US') : ''} fmtAbs={v => Math.round(v).toLocaleString('en-US')}
            compareText={p ? cmp : ''} spark={spark('orders')} loading={first} empty={!first && c && c.orders === 0} />
          <KpiCard label="Restaurant payouts owed" hint="Restaurants’ share of card orders (less debt withheld and refunds clawed back), minus the platform’s cut of cash orders they already hold. Taken from the payment split, which still charges platform promos to the restaurant."
            value={$(c?.payoutOwed)} cur={c?.payoutOwed} prev={p?.payoutOwed} prevDisplay={$(p?.payoutOwed)} fmtAbs={$}
            compareText={p ? cmp : ''} loading={first} empty={!first && c && c.orders === 0} />
        </Box>

        {/* ROW 2 — money breakdown */}
        <MoneyGrid data={summary} loading={s.loading} />

        {/* ROW 3 — waterfall */}
        <Waterfall data={summary} loading={s.loading} />

        {/* ROW 4 — trends */}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' } }}>
          <RevenueOverTime data={summary} loading={s.loading} />
          <OrderMix data={summary} loading={s.loading} />
        </Box>

        {/* ROW 5 — leaderboard */}
        <Leaderboard data={board} loading={l.loading} range={summary ? { from: summary.from, to: summary.to, compare: summary.compare } : null} />

        {/* ROW 6 — operations */}
        <OperationsCards data={ops} loading={o.loading} />

        {/* ROW 7 — recent orders */}
        <RecentOrdersTable data={ops} loading={o.loading} />
      </Box>
    </Box>
  )
}

export default withTranslation()(SuperAdminDashboard)
